import React, { useState } from 'react';
import { FiHelpCircle } from 'react-icons/fi';
import { Modal } from '../modal';
import { Card } from '../card';
import { Typography } from '../typography';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '../tabs';
import { helpTopics, HelpBlock, HelpTopic } from './helpContent';
import styles from './HelpWindow.module.css';

type IconProps = { size?: number; color?: string; style?: React.CSSProperties };
const HelpIcon = FiHelpCircle as React.FC<IconProps>;

export interface HelpWindowProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  topics?: HelpTopic[];
  children?: React.ReactNode;
}

const HelpWindow = ({
  open,
  onClose,
  title = 'Modeling Guide',
  topics: topicsProp,
  children,
}: HelpWindowProps) => {
  const topics = topicsProp ?? helpTopics;
  const [activeTopic, setActiveTopic] = useState(topics[0]?.id ?? '');
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      className={styles.panel}
    >
      <div className={styles.layout}>
        {children ?? (
          <div className={styles.guide}>
            <div className={styles.hero}>
              <span className={styles.heroIcon} aria-hidden>
                <HelpIcon size={18} />
              </span>
              <div className={styles.heroText}>
                <Typography.Heading level={4}>SCAL Modeling Guide</Typography.Heading>
                <Typography.Text color="secondary" as="p">
                  Practical reference for capillary pressure analysis, Liao modeling, and
                  Brooks-Corey interpretation.
                </Typography.Text>
              </div>
            </div>

            <Tabs value={activeTopic} onChange={setActiveTopic} variant="underline" size="sm">
              <TabList className={styles.topicTabs}>
                {topics.map(topic => (
                  <Tab key={topic.id} value={topic.id}>
                    {topic.label}
                  </Tab>
                ))}
              </TabList>

              <TabPanels className={styles.topicPanels}>
                {topics.map(topic => (
                  <TabPanel key={topic.id} value={topic.id} className={styles.topicPanel}>
                    <GuideTopic topic={topic} />
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </div>
        )}
      </div>
    </Modal>
  );
};

const renderBlock = (block: HelpBlock, index: number) => {
  if (block.type === 'paragraph') {
    return (
      <Typography.Text key={index} as="p" color="secondary" className={styles.paragraph}>
        {block.text}
      </Typography.Text>
    );
  }

  if (block.type === 'bullets') {
    return (
      <ul key={index} className={styles.bulletList}>
        {block.items.map(item => (
          <Typography.Text key={item} as="li" color="secondary">
            {item}
          </Typography.Text>
        ))}
      </ul>
    );
  }

  if (block.type === 'note') {
    return (
      <div key={index} className={styles.note}>
        <Typography.Text weight="semibold" className={styles.noteTitle}>
          {block.title}
        </Typography.Text>
        <Typography.Text as="p" color="secondary" className={styles.noteText}>
          {block.text}
        </Typography.Text>
      </div>
    );
  }

  return (
    <div key={index} className={styles.parameterGrid}>
      {block.items.map(item => (
        <Card key={item.name} variant="outlined" padding="sm" className={styles.parameterCard}>
          <Typography.Text weight="semibold">{item.name}</Typography.Text>
          <Typography.Text as="p" color="secondary" size="sm" className={styles.parameterText}>
            {item.description}
          </Typography.Text>
          <Typography.Text as="p" color="secondary" size="sm" className={styles.parameterText}>
            {item.effect}
          </Typography.Text>
        </Card>
      ))}
    </div>
  );
};

const GuideTopic = ({
  topic,
}: {
  topic: HelpTopic;
}) => (
  <div className={styles.topicLayout}>
    <aside className={styles.toc} aria-label={`${topic.title} sections`}>
      <Typography.Text label size="xs" color="muted" className={styles.tocLabel}>
        In this section
      </Typography.Text>
      <nav className={styles.tocNav}>
        {topic.sections.map(section => (
          <a key={section.id} href={`#${topic.id}-${section.id}`} className={styles.tocLink}>
            {section.title}
          </a>
        ))}
      </nav>
    </aside>

    <article className={styles.article}>
      <div className={styles.topicIntro}>
        <Typography.Heading level={3}>{topic.title}</Typography.Heading>
        <Typography.Text as="p" color="secondary" className={styles.paragraph}>
          {topic.intro}
        </Typography.Text>
      </div>

      {topic.sections.map(section => (
        <section key={section.id} id={`${topic.id}-${section.id}`} className={styles.section}>
          <Typography.Heading level={4}>{section.title}</Typography.Heading>
          <div className={styles.sectionBody}>
            {section.blocks.map(renderBlock)}
          </div>
        </section>
      ))}
    </article>
  </div>
);

export { HelpWindow };
